import React from "react";
import PropTypes from "prop-types";
import Image from "./image";
import styled from "styled-components";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import GutenbergBlocks from "./gutenberg-blocks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const Wrapper = styled.div`
  position: relative;
  max-height: 70vh;
`;
const InnerWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  height: calc(100% - 45px);
  max-height: 70vh;
  display: flex;
  align-items: center;
`;
const Video = styled.video`
  width: 100% !important;
  height: auto;
  background: var(--color-black);
`;
const SlideButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(255, 255, 255, 0.3);
  color: var(--color-white);
  height: 50px;
  width: 50px;
  border-radius: 100%;
  font-size: 2rem;
  z-index: 99;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  //Show on iPad Pro
  @media (max-width: ${({ theme }) => theme.breakpoints.phone}) {
    display: none;
  }

  &.prev {
    left: 1rem;
    svg {
      margin-right: 3px;
    }
  }
  &.next {
    right: 1rem;
    svg {
      margin-left: 3px;
    }
  }
`;
const SlideIndicator = styled.li`
  height: 4px;
  border-radius: 10px;
  background: #dbdbdb;
  display: inline-block;
  @media (min-width: 416px) {
    display: none;
  }
`;
const Children = styled.div`
  display: flex;
  justify-content: center;
  height: fit-content !important;
  align-items: center;
  flex-direction: column;
  * {
    font-family: var(--font-paragraph);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    padding: 0 1rem;
    h1,
    h2,
    h3,
    h4,
    h5 {
      font-size: 1rem;
    }
    p,
    div {
      font-size: 0.9rem;
    }
    .button-container {
      height: fit-content !important;
    }
  }
`;

export default function SuperHero({ slides, autoplay }) {
  const mediaSrc = (item) =>
    item?.localFile?.publicURL || item?.mediaItemUrl || item?.sourceUrl || "";

  return (
    <Carousel
      showStatus={false}
      infiniteLoop={true}
      showThumbs={false}
      autoPlay={autoplay}
      interval={4000}
      transitionTime={1000}
      renderArrowPrev={(onClickHandler, hasPrev, label) =>
        hasPrev && (
          <SlideButton onClick={onClickHandler} title={label} className="prev">
            <FontAwesomeIcon icon={faChevronLeft} size="sm" />
          </SlideButton>
        )
      }
      renderArrowNext={(onClickHandler, hasNext, label) =>
        hasNext && (
          <SlideButton onClick={onClickHandler} title={label} className="next">
            <FontAwesomeIcon icon={faChevronRight} size="sm" />
          </SlideButton>
        )
      }
      renderIndicator={(onClickHandler, isSelected, index, label) => {
        if (isSelected) {
          return (
            <SlideIndicator
              style={{
                background: "#343434",
                width: `calc(100%/${slides.length})`,
              }}
              aria-label={`Selected: ${label} ${index + 1}`}
              title={`Selected: ${label} ${index + 1}`}
            />
          );
        }
        return (
          <SlideIndicator
            style={{ width: `calc(100%/${slides.length})` }}
            onClick={onClickHandler}
            onKeyDown={onClickHandler}
            value={index}
            key={index}
            role="button"
            tabIndex={0}
            title={`${label} ${index + 1}`}
            aria-label={`${label} ${index + 1}`}
          />
        );
      }}
    >
      {slides?.length > 0 &&
        slides.filter(Boolean).map((slide, index) => (
          <Wrapper key={index}>
            {slide?.image?.mimeType?.includes("video") ? (
              <Video controls={true} autoPlay muted playsInline loop={true}>
                {mediaSrc(slide?.image) && (
                  <source src={mediaSrc(slide.image)} type="video/mp4" />
                )}
              </Video>
            ) : (
              <Image
                src={slide?.imageUrl}
                image={slide?.image}
                loading="eager"
              />
            )}
            {slide?.children?.length > 0 && (
              <InnerWrapper>
                <Children>
                  <GutenbergBlocks blocks={slide.children} />
                </Children>
              </InnerWrapper>
            )}
          </Wrapper>
        ))}
    </Carousel>
  );
}

SuperHero.propTypes = {
  slides: PropTypes.array,
  autoplay: PropTypes.bool,
};

SuperHero.defaultProps = {
  slides: [],
  autoplay: true,
};
